import { PILLARS, getScoreColor } from '../utils/scoring';

export default function RiskRadar({ audit }) {
  const scores = audit.scores || {};
  const ai = audit.aiResearch || {};
  const sentiment = ai.guestSentiment || {};

  const risks = [];

  // Score-based risks
  PILLARS.forEach(p => {
    const s = scores[p.id]?.score || 0;
    if (s > 0 && s < 5) {
      risks.push({
        area: p.name,
        level: 'Critical',
        current: s,
        desc: `${p.name} at ${s.toFixed(1)} — significantly below standard. Immediate action required.`,
        timeframe: 'Now',
      });
    } else if (s >= 5 && s < 6.5) {
      risks.push({
        area: p.name,
        level: 'Warning',
        current: s,
        desc: `${p.name} at ${s.toFixed(1)} — underperforming. Will become a problem if not addressed.`,
        timeframe: '30 days',
      });
    }
  });

  // Sentiment trend risks
  const negativeThemes = [
    ...(sentiment.google?.negativeThemes || []),
    ...(sentiment.combined?.negativeThemes || []),
  ].filter(Boolean);

  if (negativeThemes.length >= 3) {
    risks.push({
      area: 'Guest Sentiment',
      level: 'Warning',
      desc: `${negativeThemes.length} negative themes detected in reviews. Recurring issues: ${negativeThemes.slice(0, 3).join(', ')}. If unaddressed, expect rating decline within 3 months.`,
      timeframe: '90 days',
    });
  }

  // Concept drift risk
  const conceptScore = scores.concept_clarity?.score || 0;
  const guestScore = scores.guest_signal?.score || 0;
  if (conceptScore > 0 && guestScore > 0 && Math.abs(conceptScore - guestScore) > 2) {
    risks.push({
      area: 'Concept Drift',
      level: 'Warning',
      desc: `Gap of ${Math.abs(conceptScore - guestScore).toFixed(1)} between Concept Clarity (${conceptScore}) and Guest Signal (${guestScore}). The brand promise and guest experience are diverging.`,
      timeframe: '60 days',
    });
  }

  // Demand generation risk
  const demandScore = scores.demand_generation?.score || 0;
  if (demandScore > 0 && demandScore < 6) {
    risks.push({
      area: 'Visibility',
      level: demandScore < 4 ? 'Critical' : 'Warning',
      desc: `Demand Generation at ${demandScore.toFixed(1)}. Low visibility means declining traffic over time. Competitors with stronger marketing will capture your guests.`,
      timeframe: '60 days',
    });
  }

  // Commercial risk
  const commercialScore = scores.commercial_performance?.score || 0;
  if (commercialScore > 0 && commercialScore < 6) {
    risks.push({
      area: 'Revenue',
      level: commercialScore < 4 ? 'Critical' : 'Warning',
      desc: `Commercial Performance at ${commercialScore.toFixed(1)}. Revenue leakage is likely. Without intervention, profitability will continue to erode.`,
      timeframe: '30 days',
    });
  }

  // Sort by severity
  risks.sort((a, b) => (a.level === 'Critical' ? 0 : 1) - (b.level === 'Critical' ? 0 : 1));

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">Risk Radar</h3>
          <p className="text-xs text-neutral-500">What's coming if nothing changes</p>
        </div>
        {risks.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-400 font-bold">{risks.filter(r => r.level === 'Critical').length} critical</span>
            <span className="text-xs text-amber-400 font-bold">{risks.filter(r => r.level === 'Warning').length} warnings</span>
          </div>
        )}
      </div>

      {risks.length > 0 ? (
        <div className="space-y-2">
          {risks.map((risk, i) => (
            <div key={i} className={`rounded-lg p-3 border ${risk.level === 'Critical' ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${risk.level === 'Critical' ? 'bg-red-400/20 text-red-400' : 'bg-amber-400/20 text-amber-400'}`}>{risk.level}</span>
                <span className="text-xs text-white font-medium">{risk.area}</span>
                <span className="text-[9px] text-neutral-500 ml-auto">Act within: {risk.timeframe}</span>
              </div>
              <p className="text-xs text-neutral-400 leading-relaxed">{risk.desc}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-emerald-400 text-sm font-semibold">No immediate risks detected</p>
          <p className="text-xs text-neutral-500 mt-1">All pillars are performing at acceptable levels</p>
        </div>
      )}
    </div>
  );
}
